import { format, parse, isValid } from "date-fns";

export type DocumentType = 'Agreement' | 'TechRider' | 'Timeline';

/**
 * Generate a standardized document number in the format:
 * [DocType]-[EventDate]-[DateCreated]
 * 
 * Examples:
 * - Agreement-20260315-20260120
 * - TechRider-20260315-20260120
 * - Timeline-20260315-20260120
 */
export function generateDocumentNumber(
  docType: DocumentType,
  eventDate: Date | string | null,
  createdDate: Date = new Date()
): string {
  const eventDateStr = eventDate 
    ? formatDateForDoc(eventDate)
    : 'NODATE';
  const createdDateStr = format(createdDate, 'yyyyMMdd');
  
  return `${docType}-${eventDateStr}-${createdDateStr}`;
}

/**
 * Format a date for document naming (YYYYMMDD)
 */
function formatDateForDoc(date: Date | string): string {
  if (typeof date === 'string') {
    // Handle various date formats
    // MM/DD/YYYY
    if (date.includes('/')) {
      const parts = date.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
      }
    }
    // YYYY-MM-DD
    if (date.includes('-') && date.length >= 10) {
      return date.substring(0, 10).replace(/-/g, '');
    }
    // Try parsing as date
    try {
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, 'yyyyMMdd');
      }
    } catch {
      return 'NODATE';
    }
    return 'NODATE';
  }
  return format(date, 'yyyyMMdd');
}

/**
 * Parse a document number to extract its type and dates
 */
export function parseDocumentNumber(docNumber: string): {
  docType: DocumentType | string;
  eventDate: string | null;
  createdDate: string | null;
} {
  const parts = docNumber.split('-');
  
  if (parts.length >= 3) {
    const docType = parts[0];
    const eventDate = parts[1] !== 'NODATE' ? parts[1] : null;
    const createdDate = parts[2];
    return { docType, eventDate, createdDate };
  }
  
  // Legacy format fallback
  return { docType: docNumber, eventDate: null, createdDate: null };
}

/**
 * Extract event date from document for grouping purposes
 * PRIORITY: event_details first (source of truth), then fall back to document number parsing
 * Returns null if no event date can be determined
 */
export function getEventDateFromDocument(contract: any): string | null {
  // FIRST: Try to get from event_details (this is the source of truth)
  if (contract.event_details) {
    // Check for specific event date in details (stored as YYYY-MM-DD or similar)
    if (contract.event_details.eventDate) {
      // Normalize to YYYY-MM-DD format
      const dateStr = contract.event_details.eventDate;
      if (typeof dateStr === 'string') {
        // Already in YYYY-MM-DD format
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        // Handle YYYYMMDD format
        if (dateStr.match(/^\d{8}$/)) {
          return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
        }
        // Handle MM/DD/YYYY format
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const [month, day, year] = parts;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        }
        // Handle "MMM d, yyyy" format (e.g., "Jan 31, 2026")
        const mmmDYyyy = parse(dateStr, 'MMM d, yyyy', new Date());
        if (isValid(mmmDYyyy)) {
          return format(mmmDYyyy, 'yyyy-MM-dd');
        }
        // Handle "MMMM d, yyyy" format (e.g., "January 31, 2026")
        const mmmmDYyyy = parse(dateStr, 'MMMM d, yyyy', new Date());
        if (isValid(mmmmDYyyy)) {
          return format(mmmmDYyyy, 'yyyy-MM-dd');
        }
        // Try generic Date parsing as last resort
        try {
          const parsed = new Date(dateStr);
          if (isValid(parsed)) {
            return format(parsed, 'yyyy-MM-dd');
          }
        } catch {
          // Fall through
        }
      }
      return null;
    }
    // Check for events array
    if (Array.isArray(contract.event_details) && contract.event_details[0]?.date) {
      return contract.event_details[0].date;
    }
  }
  
  // SECOND: Try to parse from the new naming convention (only if event_details doesn't have it)
  const parsed = parseDocumentNumber(contract.contract_number || '');
  // Only use document number parsing for NEW format documents (starts with Agreement-, TechRider-, Timeline-)
  const hasNewFormat = contract.contract_number?.match(/^(Agreement|TechRider|Timeline)-/);
  if (hasNewFormat && parsed.eventDate && parsed.eventDate !== 'NODATE') {
    // Convert YYYYMMDD to YYYY-MM-DD format
    const year = parsed.eventDate.substring(0, 4);
    const month = parsed.eventDate.substring(4, 6);
    const day = parsed.eventDate.substring(6, 8);
    return `${year}-${month}-${day}`;
  }
  
  return null;
}

/**
 * Extract event name/occasion from document for grouping purposes
 */
export function getEventNameFromDocument(contract: any): string | null {
  if (contract.event_details) {
    // Check for occasion/event name (different document types store this differently)
    if (contract.event_details.occasion) {
      return contract.event_details.occasion;
    }
    if (contract.event_details.eventName) {
      return contract.event_details.eventName;
    }
    // Check for events array (Agreements store event data this way)
    if (Array.isArray(contract.event_details) && contract.event_details[0]?.occasion) {
      return contract.event_details[0].occasion;
    }
    // Check for events property within event_details
    if (contract.event_details.events && Array.isArray(contract.event_details.events)) {
      if (contract.event_details.events[0]?.occasion) {
        return contract.event_details.events[0].occasion;
      }
    }
  }
  return null;
}

/**
 * Generate a group key combining event name and date
 */
export function getEventGroupKey(contract: any): string {
  const eventDate = getEventDateFromDocument(contract);
  const eventName = getEventNameFromDocument(contract);
  
  if (eventName && eventDate) {
    return `${eventName}|||${eventDate}`;
  }
  if (eventDate) {
    return `Event|||${eventDate}`;
  }
  return 'ungrouped';
}

/**
 * Parse event group key back to name and date
 */
export function parseEventGroupKey(key: string): { name: string; date: string | null } {
  if (key === 'ungrouped') {
    return { name: 'Other Documents', date: null };
  }
  const [name, date] = key.split('|||');
  return { name: name || 'Event', date: date || null };
}

/**
 * Format a date string for display (e.g., "Mar 15, 2026")
 */
export function formatEventDateForDisplay(dateStr: string | null): string {
  if (!dateStr) return 'No Event Date';
  
  try {
    // Handle YYYY-MM-DD
    if (dateStr.includes('-')) {
      const date = new Date(dateStr + 'T00:00:00');
      return format(date, 'MMM d, yyyy');
    }
    // Handle YYYYMMDD
    if (dateStr.length === 8) {
      const year = parseInt(dateStr.substring(0, 4));
      const month = parseInt(dateStr.substring(4, 6)) - 1;
      const day = parseInt(dateStr.substring(6, 8));
      return format(new Date(year, month, day), 'MMM d, yyyy');
    }
    // Handle MM/DD/YYYY
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/');
      return format(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)), 'MMM d, yyyy');
    }
    return dateStr;
  } catch {
    return dateStr;
  }
}

/**
 * Get display label for document type
 */
export function getDocumentTypeLabel(docNumber: string): string {
  if (docNumber.startsWith('Agreement-')) return 'Agreement';
  if (docNumber.startsWith('TechRider-')) return 'Tech Rider';
  if (docNumber.startsWith('Timeline-')) return 'Timeline';
  
  // Legacy formats
  if (docNumber.startsWith('ITIN-')) return 'Timeline';
  if (docNumber.startsWith('TECH-')) return 'Tech Rider';
  if (docNumber.startsWith('LSC-')) return 'Agreement';
  
  return 'Document';
}
